import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// Define the initial state of the form
const initialState = {
    staffData: {
        // _id: '',
        name: '',
        email: '',
        type: 'host',
        sellerId: '',
        password: '',
        confirmPassword: '',
        permissions: [],
    },
    status: 'idle', // 'idle' | 'loading' | 'success' | 'failed'
    error: null,
    validationErrors: {}, // Add a new field for validation errors
};

// Async thunk for fetching the staff form data
export const fetchStaffData = createAsyncThunk(
    'action/fetchStaffData',
    async ({staffId, customFetch}, { rejectWithValue }) => {
        try {
            // Replace with your API endpoint
            const response = await customFetch(`/api/staff/${staffId}`);
            if (!response.ok) {
                throw new Error('Failed to fetch staff data');
            }
            return await response.json();
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// Async thunk for submitting the form data
export const submitStaffForm = createAsyncThunk(
    'action/submitStaffForm',
    async ({filledFormData, customFetch}, { rejectWithValue, getState }) => {
        const { validationErrors, staffData } = getState().staff;
        if (Object.keys(validationErrors).length > 0) {
            return rejectWithValue(Object.values(validationErrors)[0]);
        }
        try {
            // Replace with your API call logic
            const response = await customFetch('/api/staff', {
                method: 'POST',
                body: JSON.stringify(filledFormData),
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            let jsonResponse  = await response.json()
            if (!response.ok) {
                throw new Error(jsonResponse?.message || 'Failed to create staff member');
            }
            return jsonResponse;
        } catch (error) {
            // console.error(error)
            return rejectWithValue(error.message);
        }
    }
);

// Async thunk for updating the form data
export const updateStaffForm = createAsyncThunk(
    'action/updateStaffForm',
    async ({updatedFormData, staffId, customFetch}, { rejectWithValue, getState }) => {
        const { validationErrors, staffData } = getState().staff;
        if (Object.keys(validationErrors).length > 0) {
            return rejectWithValue(Object.values(validationErrors)[0]);
        }
        try {
            const response = await customFetch(`/api/staff/${staffId}`, {
                method: 'PUT',
                body: JSON.stringify(updatedFormData),
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            let jsonResponse  = await response.json()
            if (!response.ok) {
                throw new Error(jsonResponse?.message || 'Failed to update staff member');
            }
            return jsonResponse;
        } catch (error) {
            // console.error(error)
            return rejectWithValue(error.message);
        }
    }
);

// Async thunk for delete the staff
export const deleteStaffMember = createAsyncThunk(
    'action/deleteStaff',
    async ({staffId, customFetch} , { rejectWithValue, getState }) => {
        try {
            const response = await customFetch(`/api/staff/${staffId}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            let jsonResponse  = await response.json()
            if (!response.ok) {
                throw new Error(jsonResponse?.message || 'Failed to delete staff member');
            }
            return jsonResponse;
        } catch (error) {
            // console.error(error)
            return rejectWithValue(error.message);
        }
    }
);

// Async thunk for update Password 
export const updateStaffPassword = createAsyncThunk(
    'action/updateStaffPassword',
    async ({passwordData, staffId, customFetch}, { rejectWithValue, getState }) => {
        const { validationErrors, staffData} = getState().staff;
        if(Object.keys(validationErrors).length > 0) {
            return rejectWithValue(Object.values(validationErrors)[0]);                                                                                                                                                                                                                                                                                             
        } 
        try {
            const response = await customFetch('/api/staff/password', {
                method: 'POST',
                body: JSON.stringify({...passwordData, staffId: staffId}),
                headers: {'Content-Type': 'application/json'}
            })
            let jsonResponse = await response.json()
            if(!response.ok) {
                throw new Error(jsonResponse?.message || 'Failed to change password');
            }
            return jsonResponse
        } catch(err) {
            return rejectWithValue(err.message)
        }
    }
);

// Async thunk for updating the permissions
export const updateStaffPermissions = createAsyncThunk(
    'action/updateStaffPermissions',
    async ({permissions, staffId, customFetch}, { rejectWithValue, getState }) => {
        try {
            const response = await customFetch(`/api/staff/permissions`, {
                method: 'PUT',
                body: JSON.stringify({permissions: permissions, staffId: staffId}),
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            let jsonResponse = await response.json()
            if (!response.ok) {
                throw new Error(jsonResponse?.message || 'Failed to update staff permissions');
            }
            return jsonResponse;
        } catch (error) {
            console.error(error)
            return rejectWithValue(err.message)
        }
    }
)

// The slice for the form
const staffSlice = createSlice({
    name: 'staffSlice',
    initialState,
    reducers: {
        // Reducer to update form data and validate fields
        updateAndValidateStaffFormData: (state, action) => {
            const { name, value } = action.payload;
            state.staffData[name] = value;
            // Validate the field
            if (!value.trim()) {
                state.validationErrors[name] = `${name} is required`;
                return;
            } else {
                delete state.validationErrors[name]
            }
            const emailRegex = /^([a-zA-Z0-9._%-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})$/;
            if(name == 'email' && !emailRegex.test(value.trim())) {
                state.validationErrors[name] = `Enter a valid email address`;
            }
        },
        resetStaffForm: (state, action) => {
            Object.keys(initialState).forEach((key) => {
                state[key] = initialState[key]
            })
        },
        addStaffValidationError: (state, action) => {
            state.validationErrors = {
                name: 'name is required',
                email: 'email is required',
                sellerId: 'Seller Id is required'
            }
            if(action.payload?.role === 'app') {
                delete state.validationErrors['sellerId']
            }
        },
        addStaffPasswordValidationError: (state, action) => {
            state.validationErrors = {
                password: 'password is required',
                confirmPassword: 'Confirm password is required'
            }
        },
        updateStaffPasswordValidationError: (state, action) => {
            const {name, value} = action.payload
            state.staffData[name] = value
            if (!value) {
                state.validationErrors[name] = `${name} is required`;
                if(name == 'confirmPassword')
                    state.validationErrors[name] = `Confirm password is required`
                return;
            } else {
                delete state.validationErrors[name]
            }
            if(name == 'confirmPassword' && state.staffData.password != value) {
                state.validationErrors[name] = `Password and confirm password is not match`
            }
        },
        addStaffData: (state, action) => {
            state.staffData = {...state.staffData, ...action.payload}
        },
        
    },
    extraReducers: (builder) => {
        builder
            .addCase(submitStaffForm.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(submitStaffForm.fulfilled, (state, action) => {
                console.log('action.payload.data',action.payload)
                state.status = `success,${action.payload?.message}`;
                state.validationErrors = {}; // Clear validation errors
            })
            .addCase(submitStaffForm.rejected, (state, action) => {
                state.status = `failed,${action.payload}`;
                state.error = action.payload;
            })
            .addCase(updateStaffForm.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(updateStaffForm.fulfilled, (state, action) => {
                state.status = `success,${action.payload?.message}`;
                state.validationErrors = {}; // Clear validation errors
            })
            .addCase(updateStaffForm.rejected, (state, action) => {
                state.status = `failed,${action.payload}`;
                state.error = action.payload;
            })
            .addCase(fetchStaffData.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchStaffData.fulfilled, (state, action) => {
                state.status = 'success';
                state.staffData = data;
            })
            .addCase(fetchStaffData.rejected, (state, action) => {
                state.status = `failed,${action.payload}`;
                state.error = action.payload;
            })
            .addCase(deleteStaffMember.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(deleteStaffMember.fulfilled, (state, action) => {
                state.status = `success,${action.payload?.message}`;
            })
            .addCase(deleteStaffMember.rejected, (state, action) => {
                state.status = `failed,${action.payload}`;
                state.error = action.payload;
            })
            .addCase(updateStaffPassword.pending, (state, action) => {
                state.status = 'loading'
            })
            .addCase(updateStaffPassword.fulfilled, (state, action) => {
                state.status = `success,${action.payload?.message}`
            })
            .addCase(updateStaffPassword.rejected, (state, action) => {
                state.status = `failed,${action.payload}`;
                state.error = action.payload
            })
            .addCase(updateStaffPermissions.pending, (state, action) => {
                state.status = 'loading'
            })
            .addCase(updateStaffPermissions.fulfilled, (state, action) => {
                state.status = `success,${action.payload?.message}`
            })
            .addCase(updateStaffPermissions.rejected, (state, action) => {
                state.status = `failed,${action.payload}`;
                state.error = action.payload
            })
    }
});

export const { updateAndValidateStaffFormData, resetStaffForm, addStaffValidationError, addStaffData, addStaffPasswordValidationError, updateStaffPasswordValidationError } = staffSlice.actions;
export default staffSlice.reducer
